import { useEffect, useState } from 'react'
import convert from 'xml-js'
import GetUrls from 'ts/enums/Url'
import { ButtonStyle } from 'ts/types'
import useModalStore from 'store/modal'
import useModalForm from 'hooks/useModalForm'
import {
  ButtonInnerXml,
  GradientStop,
  LinearGradientBrush
} from 'ts/types/ButtonStyle'
import { CheckBox, Input, InputColor, Select } from 'components/ui'
import ButtonStyleItem from 'components/ButtonStyleItem'
import { ButtonType } from 'ts/enums/ButtonType'

type Gradient = {
  gradientFrom: string
  gradientTo: string
}

const defaultColor = "#FFFFFF"
const defaultArray = [
  {
      "type": "element",
      "name": "SolidColorBrush",
      "attributes": {
          "xmlns": "http://schemas.microsoft.com/winfx/2006/xaml/presentation"
      },
      "elements": [
          {
              "type": "text",
              "text": "#FFFF8585"
          }
      ]
  }
]

const objectToStore = {
  foreground: "<SolidColorBrush xmlns=\"http://schemas.microsoft.com/winfx/2006/xaml/presentation\">#FFFFFFFF</SolidColorBrush>",
  borderstyle: "<SolidColorBrush xmlns=\"http://schemas.microsoft.com/winfx/2006/xaml/presentation\">#FFFFFFFF</SolidColorBrush>",
  text: "<SolidColorBrush xmlns=\"http://schemas.microsoft.com/winfx/2006/xaml/presentation\">#FF000000</SolidColorBrush>",
};

const getStyleType = (foregroundValue: string) => {
  // If it's a simple hex color, treat it as SolidColorBrush
  if (foregroundValue && foregroundValue.startsWith('#')) {
    return defaultArray[0]
  }
  
  try {
    const foreground = convert.xml2js(foregroundValue)
    if (
      foreground &&
      foreground.elements &&
      foreground.elements[0]
    ) {
      return foreground.elements[0];
    }
  } catch (error) {
    console.warn('Failed to parse foreground as XML:', foregroundValue, error)
  }
  
  return defaultArray[0]
}

const getForegroundColor = (foregroundValue: string) => {
  // If it's a simple hex color, return it directly
  if (foregroundValue && foregroundValue.startsWith('#')) {
    return {
      foreground: foregroundValue
    }
  }

  const foregroundXml = getStyleType(foregroundValue)

  let foregroundInner
  let gradientFrom
  let gradientTo

  switch (foregroundXml.name) {
    case ButtonType.solidColorBrush:
      foregroundInner = foregroundXml.elements[0] as ButtonInnerXml

      return {
        foreground: `#${foregroundInner.text.substring(3)}`
      }

    case ButtonType.linearGradientBrush:
      foregroundInner = foregroundXml.elements[0] as LinearGradientBrush
      gradientFrom = foregroundInner.elements[0] as GradientStop
      gradientTo = foregroundInner.elements[1] as GradientStop

      return {
        gradientFrom: gradientFrom
          ? `#${gradientFrom.attributes.Color.substring(3)}`
          : defaultColor,
        gradientTo: gradientTo
          ? `#${gradientTo.attributes.Color.substring(3)}`
          : defaultColor
      }
    default:
      return {}
  }
}

const ButtonStyleAddPage = () => {
  const Url = GetUrls();
  const params = useModalStore((state) => state.params) as ButtonStyle
  const setParams = useModalStore((state) => state.setParams)

  useEffect(() => {
    if(!params){
      setParams(objectToStore)
    }
  }, [])
  
  const { ModalForm, form } = useModalForm()
  const { setValue, register } = form

  console.log("params test",params);
  const [type, setType] = useState(
    params ? getStyleType(params.foreground).name : "SolidColorBrush"
  )
  
  console.log("initial params:", params)
  if (params) {
    setValue('buttonStyleId', params.buttonStyleId)
  }

  const onColorChange = (
    buttonType: ButtonType,
    key: string,
    color: string | Gradient
  ) => {
    let xml = ''

    if (buttonType === ButtonType.solidColorBrush) {
      xml = `<SolidColorBrush xmlns="http://schemas.microsoft.com/winfx/2006/xaml/presentation">#FF${(
        color as string
      )
        .substring(1)
        .toUpperCase()}</SolidColorBrush>`
    }

    if (buttonType === ButtonType.linearGradientBrush) {
      xml = `<LinearGradientBrush EndPoint="6.12303176911189E-17,1" xmlns="http://schemas.microsoft.com/winfx/2006/xaml/presentation"><LinearGradientBrush.GradientStops><GradientStop Color="#FF${(
        color as Gradient
      ).gradientFrom
        .substring(1)
        .toUpperCase()}" Offset="0" /><GradientStop Color="#FF${(
        color as Gradient
      ).gradientTo
        .substring(1)
        .toUpperCase()}" Offset="1" /></LinearGradientBrush.GradientStops></LinearGradientBrush>`
    }

    setValue(key, xml)
    setParams({
      ...params,
      [key]: xml
    })
  }

  const getBorderColor = (borderStyleValue: string) => {
    if (!borderStyleValue) return "#000000"
    
    // Check if it's a simple hex color (starts with #)
    if (borderStyleValue.startsWith('#')) {
      return borderStyleValue
    }
    
    // Try to parse as XML
    try {
      const borderstyle = convert.xml2js(borderStyleValue)
      if (
        borderstyle &&
        borderstyle.elements &&
        borderstyle.elements[0] &&
        borderstyle.elements[0].elements &&
        borderstyle.elements[0].elements[0] &&
        borderstyle.elements[0].elements[0].text
      ) {
        return `#${borderstyle.elements[0].elements[0].text.substring(3)}`
      }
    } catch (error) {
      console.warn('Failed to parse border color as XML:', borderStyleValue, error)
    }
    
    return "#000000"
  }

  const getTextColor = (textValue: string) => {
    if (!textValue) return "#000000"
    
    // Check if it's a simple hex color (starts with #)
    if (textValue.startsWith('#')) {
      return textValue
    }
    
    // Try to parse as XML
    try {
      const text = convert.xml2js(textValue)
      if (
        text &&
        text.elements &&
        text.elements[0] &&
        text.elements[0].elements &&
        text.elements[0].elements[0] &&
        text.elements[0].elements[0].text
      ) {
        return `#${text.elements[0].elements[0].text.substring(3)}`
      }
    } catch (error) {
      console.warn('Failed to parse text color as XML:', textValue, error)
    }
    
    return "#000000"
  }

  return (
    <ModalForm
      config={{ url: Url.postButtonStyle, method: params?.buttonStyleId ? 'PUT' : 'POST' }}
      title={`${params ? 'Edit ' : 'Create ' }Button Style`}
      description='Customize a button style'
    >
      <div className='flex gap-8'> 
        <CheckBox
          defaultChecked={params?.flActive || false}
          label="Active"
          {...register('flActive')}
        />
        {params ? (
          <Input defaultValue={params.buttonStyleId} label="ID" disabled />
        ) : null}
      </div>
      <Input
        defaultValue={params?.name || ''}
        label="Button Style Name"
        {...register('name', { required: true })}
      />
      <Select
        label="Style Type"
        defaultValue={type}
        options={Object.values(ButtonType).map((key) => ({
          value: key.toString(),
          children: key
        }))}
        onChange={(e) => setType(e.currentTarget.value as ButtonType)}
      />

      {type === ButtonType.solidColorBrush ? (
        <InputColor
          label="Background Color"
          defaultValue={
            params
              ? getForegroundColor(params.foreground).foreground
              : defaultColor
          }
          onBlur={(e) =>
            onColorChange(
              ButtonType.solidColorBrush,
              'foreground',
              e.currentTarget.value
            )
          }
        />
      ) : (
        <>
          <InputColor
            label="Gradient From"
            defaultValue={
              params
                ? getForegroundColor(params?.foreground).gradientFrom
                : '#fff'
            }
            onBlur={(e) =>
              onColorChange(ButtonType.linearGradientBrush, 'foreground', {
                gradientFrom: e.currentTarget.value,
                gradientTo:
                  getForegroundColor(params?.foreground).gradientTo ||
                  defaultColor
              })
            }
          />
          <InputColor
            label="Gradient To"
            defaultValue={
              params
                ? getForegroundColor(params?.foreground).gradientTo
                : defaultColor
            }
            onBlur={(e) =>
              onColorChange(ButtonType.linearGradientBrush, 'foreground', {
                gradientFrom:
                  getForegroundColor(params?.foreground).gradientFrom ||
                  defaultColor,
                gradientTo: e.currentTarget.value
              })
            }
          />
        </>
      )}

      <InputColor
        label="Border Color"
        defaultValue={
          params ? getBorderColor(params.borderstyle) : defaultColor
        }
        onBlur={(e) =>
          onColorChange(
            ButtonType.solidColorBrush,
            'borderstyle',
            e.currentTarget.value
          )
        }
      />
      <InputColor
        label="Text Color"
        defaultValue={params ? getTextColor(params.text) : defaultColor}
        onBlur={(e) =>
          onColorChange(
            ButtonType.solidColorBrush,
            'text',
            e.currentTarget.value
          )
        }
      />
      {params ? <ButtonStyleItem data={params} /> : null}
    </ModalForm>
  )
}

export default ButtonStyleAddPage
